import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { Alert, Button as AntdButton, Modal, Skeleton, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { CloseXIcon } from '~/img';
import { useDirection } from '~/hooks/i18nHooks';
import colors from '~/constants/colors';
import theme from '~/constants/theme';
import longdash from '~/constants/longdash';
import { useIsMobile } from '~/hooks/useScreenSizes';
import { useGetFeesAndCharges } from '~/controllers/contract';
import { FeeAmount, FeeLine, FeeProduct } from '~/api/contract';
import { SaudiReyalSymbolIcon } from '~/img';

interface Props {
  open: boolean;
  onAgree: () => void;
  onClose: () => void;
}

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const renderAmountValue = (
  amount: FeeAmount,
  dir?: string,
  iconStyle?: React.CSSProperties,
  isCapNote?: boolean
) => {
  if (amount.currency === 'SAR') {
    const isRtl = dir === 'rtl';
    const baseIconStyle: React.CSSProperties = { fontSize: 11, verticalAlign: 'middle' };
    const mergedIconStyle = { ...baseIconStyle, ...iconStyle };

    // For cap notes in RTL (top 2 points: "بحد أقصى 160 ﷼", "بحد أقصى 80 ﷼"): number first, icon on right
    if (isRtl && isCapNote) {
      return (
        <>
          <span>{amount.value}</span>
          <SaudiReyalSymbolIcon style={{ ...mergedIconStyle, marginLeft: 4 }} />
        </>
      );
    }

    // For main amounts (bottom 3 points: "2.5% + ﷼ 1", "﷼ 150", "﷼ 6"): icon first, then number
    return (
      <>
        <SaudiReyalSymbolIcon style={{ ...mergedIconStyle, marginRight: 4 }} />
        <span>{amount.value}</span>
      </>
    );
  }

  return (
    <span>
      {amount.currency} {amount.value}
    </span>
  );
};

/** Replaces "SAR", "{SAR}", or "﷼" tokens in a translated string with the Saudi Riyal icon inline */
const renderTextWithSarIcon = (text: string): React.ReactNode => {
  if (!text) return text;
  const regex = /(\s*(?:SAR|\{SAR\}|﷼)\s*)/g;
  const parts = text.split(regex);
  if (parts.length === 1) return text;

  return (
    <>
      {parts.map((part, i) => {
        const trimmed = part.trim();
        if (trimmed === 'SAR' || trimmed === '{SAR}' || trimmed === '﷼') {
          return (
            <SaudiReyalSymbolIcon
              key={i}
              style={{ fontSize: 11, verticalAlign: 'middle', marginInline: 3 }}
            />
          );
        }
        return part;
      })}
    </>
  );
};

/** Renders the value cell for a single fee line */
const FeeLineValue: FC<{
  line: FeeLine;
  waivedLabel: string;
  defaultCurrency: string;
  cappedAtLabel: string;
  dir?: string;
}> = ({ line, waivedLabel, defaultCurrency, cappedAtLabel, dir }) => {
  // Explicit waived flag from API
  if (line.waived) {
    return <FeeValue className="waived" dir={dir}>{waivedLabel}</FeeValue>;
  }

  // Amount = 0 with no percent also means "Waived" (matches Figma: Setup Fee, Monthly Fee)
  if (
    line.amount !== undefined &&
    line.amount !== null &&
    line.amount.value === 0 &&
    (line.percent === undefined || line.percent === null)
  ) {
    return <FeeValue className="waived" dir={dir}>{waivedLabel}</FeeValue>;
  }

  const parts: React.ReactNode[] = [];

  if (line.percent !== undefined && line.percent !== null) {
    parts.push(<span key="pct">{line.percent}%</span>);
  }

  if (line.amount && line.amount.value > 0) {
    const amount: FeeAmount = {
      value: line.amount.value,
      currency: line.amount.currency ?? defaultCurrency,
    };

    if (parts.length > 0) parts.push(<span key="plus">&nbsp;+&nbsp;</span>);
    parts.push(<React.Fragment key="amt">{renderAmountValue(amount, dir)}</React.Fragment>);
  }

  if (parts.length === 0) {
    return <FeeValue dir={dir}>{longdash}</FeeValue>;
  }

  return (
    <FeeValueContainer dir={dir}>
      <FeeValue dir={dir}>
        <FeeValueInner>{parts}</FeeValueInner>
      </FeeValue>
      {line.amountCap && (
        <FeeCapNote dir={dir}>
          <span className="capLabel">{cappedAtLabel}</span>
          <span className="capAmount">
            {renderAmountValue(
              {
                value: line.amountCap.value,
                currency: line.amountCap.currency ?? defaultCurrency,
              },
              dir,
              { fontSize: 12, color: '#8F8F8F' },
              true
            )}
          </span>
        </FeeCapNote>
      )}
    </FeeValueContainer>
  );
};

/** Normalized keys that are always pinned to the top of the fee list */
const SETUP_FEE_NORMALIZED = ['setupfee', 'setupfees'];

/** Returns true when a fee line label (i18n key or plain text) represents a setup fee. */
const isSetupFeeLine = (label: string): boolean => {
  const normalized = label.toLowerCase().replace(/[._\-\s]/g, '');
  return SETUP_FEE_NORMALIZED.some((key) => normalized.includes(key));
};

/** Renders all sections inside one product */
const ProductSections: FC<{
  product: FeeProduct;
  waivedLabel: string;
  defaultCurrency: string;
  cappedAtLabel: string;
  dir?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ product, waivedLabel, defaultCurrency, cappedAtLabel, dir, t }) => (
  <>
    {product.sections.map((section) => {
      // Pin setup fee lines to the top, keep everything else in original order
      const sortedLines = [...section.lines].sort((a, b) => {
        const aIsSetup = isSetupFeeLine(a.label);
        const bIsSetup = isSetupFeeLine(b.label);
        if (aIsSetup && !bIsSetup) return -1;
        if (!aIsSetup && bIsSetup) return 1;
        return 0;
      });

      return (
        <ServiceGroup key={`${product.key}-section-${section.label}`}>
          {/* <ServiceGroupHeader dir={dir}>{t(section.label, { defaultValue: section.label })}</ServiceGroupHeader> */}

          {sortedLines.map((line) => (
            <React.Fragment key={`${product.key}-line-${section.label}-${line.label}`}>
              <FeeRow dir={dir}>
                <FeeLabel dir={dir}>
                  <span className="FeeRow-Name">
                    {renderTextWithSarIcon(t(line.label, { defaultValue: line.label }))}
                  </span>
                  {line.condition && (
                    <span className="FeeRow-Desc">
                      {renderTextWithSarIcon(t(line.condition, { defaultValue: line.condition }))}
                    </span>
                  )}
                </FeeLabel>
                <FeeLineValue
                  line={line}
                  waivedLabel={waivedLabel}
                  defaultCurrency={defaultCurrency}
                  cappedAtLabel={cappedAtLabel}
                  dir={dir}
                />
              </FeeRow>
            </React.Fragment>
          ))}
        </ServiceGroup>
      );
    })}
  </>
);

const hasFeeContent = (products: FeeProduct[]) =>
  products.some((product) =>
    product.sections?.some((section) => section.lines && section.lines.length > 0),
  );

/* ─── component ───────────────────────────────────────────────────────────── */

const FeesAndChargesDrawer: FC<Props> = ({ open, onAgree, onClose }) => {
  const { t } = useTranslation('ksaApplication');
  const dir = useDirection();
  const isMobile = useIsMobile();

  const feesQuery = useGetFeesAndCharges(open);

  const products = React.useMemo(
    () => feesQuery.data?.products ?? [],
    [feesQuery.data?.products]
  );
  const defaultCurrency = feesQuery.data?.currency ?? 'SAR';
  const vatPercent = feesQuery.data?.vat?.percent;
  const footer = feesQuery.data?.footer ?? t('All prices are VAT excluded');

  // Initialise the active tab whenever the products list first loads.
  // We intentionally exclude activeTabKey from deps so a manual tab
  // switch is not overridden on subsequent renders.
  const [activeTabKey, setActiveTabKey] = useState<string>('');

  useEffect(() => {
    if (products.length > 0) {
      setActiveTabKey((prev) => (prev ? prev : products[0].key));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const currentTabKey = products.some((p) => p.key === activeTabKey)
    ? activeTabKey
    : products[0]?.key ?? '';
  const activeProduct = products.find((p) => p.key === currentTabKey) || products[0];

  const renderBody = () => {
    if (feesQuery.isLoading) {
      return <Skeleton active paragraph={{ rows: 10 }} />;
    }

    if (feesQuery.isError) {
      return (
        <>
          <Alert
            type="warning"
            message={t('Could not load fees information from server. Please try again.')}
            style={{ marginBottom: 12, fontSize: 12 }}
          />
          <RetryBtn
            type="link"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => {
              void feesQuery.refetch();
            }}
            loading={feesQuery.isFetching}
          >
            {t('Retry')}
          </RetryBtn>
        </>
      );
    }

    if (!hasFeeContent(products)) {
      return (
        <Typography.Text type="secondary">{t('No fees information available.')}</Typography.Text>
      );
    }

    return (
      <>
        {products.length > 1 && (
          <TabsRow dir={dir}>
            {products.map((product) => (
              <TabPill
                key={product.key}
                active={currentTabKey === product.key}
                onClick={() => setActiveTabKey(product.key)}
              >
                {product.key === 'eCommerce'
                  ? t('eCommerce_tab_label')
                  : product.key === 'POS'
                    ? t('POS_tab_label')
                    : product.label ?? product.key}
              </TabPill>
            ))}
          </TabsRow>
        )}

        {activeProduct && (
          <ProductSections
            product={activeProduct}
            dir={dir}
            waivedLabel={t('Waived')}
            defaultCurrency={defaultCurrency}
            cappedAtLabel={t('capped at')}
            t={t}
          />
        )}

        {vatPercent !== undefined && vatPercent !== null && (
          <FeeRow dir={dir}>
            <FeeLabel dir={dir}>
              <span className="FeeRow-Name">{t('VAT')}</span>
            </FeeLabel>
            <FeeValueContainer dir={dir}>
              <FeeValue dir={dir}>{vatPercent}%</FeeValue>
            </FeeValueContainer>
          </FeeRow>
        )}

        <VatNote dir={dir}>{footer ? t(footer) : ''}</VatNote>
      </>
    );
  };

  return (
    <StyledModal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered={!isMobile}
      isMobile={isMobile}
      width={isMobile ? '100vw' : 760}
      style={isMobile ? { top: 'auto', bottom: 0, margin: 0, paddingBottom: 0, maxWidth: '100vw' } : undefined}
    >
      <ModalHeaderRow dir={dir}>
        <Typography.Text className="ModalTitle-Text">{t('Fees and charges')}</Typography.Text>
        <CloseBtn onClick={onClose} id="feesDrawer_closeBtn">
          <CloseXIcon />
        </CloseBtn>
      </ModalHeaderRow>

      <ModalBody dir={dir}>{renderBody()}</ModalBody>

      <FooterArea>
        <AgreeButton
          id="feesDrawer_agreeButton"
          type="primary"
          block
          onClick={onAgree}
          loading={feesQuery.isLoading}
          disabled={feesQuery.isError || feesQuery.isLoading}
        >
          {t('I Agree')}
        </AgreeButton>
      </FooterArea>
    </StyledModal>
  );
};

/* ─── Styled components ─────────────────────────────────────── */

const StyledModal = styled(Modal) <{ isMobile?: boolean }>`
  .ant-modal-content {
    border-radius: ${({ isMobile }) => (isMobile ? '20px 20px 0 0' : '24px')};
    overflow: hidden;
  }
  .ant-modal-body {
    padding: ${({ isMobile }) => (isMobile ? '16px 16px 32px 16px' : '40px')};
  }

  @media (max-width: ${theme.breakpoints.sm}px) {
    position: fixed !important;
    bottom: 0 !important;
    top: auto !important;
    left: 0 !important;
    right: 0 !important;
    margin: 0 !important;
    width: 100vw !important;
    max-width: 100vw !important;
    padding-bottom: 0 !important;

    .ant-modal-content {
      border-radius: 20px 20px 0 0;
    }
  }
`;

const ModalHeaderRow = styled.div<{ dir?: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  direction: ${({ dir }) => dir ?? 'ltr'};

  .ModalTitle-Text {
    font-family: Inter, sans-serif;
    font-size: 24px;
    font-weight: 500;
    color: #000;
    line-height: 40px;

    @media (min-width: ${theme.breakpoints.sm}px) {
      font-size: 20px;
    }
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  &:hover {
    // background: #f5f5f5;
  }
`;

const ModalBody = styled.div<{ dir?: string }>`
  direction: ${({ dir }) => dir ?? 'ltr'};
  /* Enough height for ~5-6 fee rows on small screens, more on desktop */
  max-height: 52vh;
  overflow-y: auto;
  padding-inline-end: 4px;

  @media (min-width: ${theme.breakpoints.sm}px) {
    max-height: 60vh;
  }
`;

const TabsRow = styled.div<{ dir?: string }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 20px;
`;

const TabPill = styled.div<{ active: boolean; dir?: string }>`
  padding: 8px 20px;
  border-radius: 100px;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background-color: ${({ active }) => (active ? colors.primaryColor : '#E6F0F7')};
  color: ${({ active }) => (active ? '#ffffff' : '#4A4A4A')};

  &:hover {
    opacity: 0.9;
  }

  &:lang(ar) {
    font-family: Tajawal, sans-serif;
  }
`;

const ServiceGroup = styled.div`
  margin-bottom: 4px;
`;

const ServiceGroupHeader = styled.div<{ dir?: string }>`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #1a1a2e;
  background: #eef4fd;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 2px;
  margin-top: 4px;
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  flex-direction: ${({ dir }) => (dir === 'rtl' ? 'row-reverse' : 'row')};

  @media (min-width: ${theme.breakpoints.sm}px) {
    font-size: 14px;
    padding: 11px 16px;
  }
`;

const FeeRow = styled.div<{ dir?: string }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 0;
  // border-bottom: 1px dashed #E8E8E8;

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: ${theme.breakpoints.sm}px) {
    gap: 12px;
    padding: 18px 0;
  }
`;

const FeeLabel = styled.div<{ dir?: string }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};

  .FeeRow-Name {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #070707;
    line-height: 20px;
    word-break: break-word;

    @media (min-width: ${theme.breakpoints.sm}px) {
      font-size: 14px;
    }
  }
  .FeeRow-Desc {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 11px;
    color: #8c8c8c;
    line-height: 18px;
    margin-top: 2px;
  }
`;

const FeeValueContainer = styled.div<{ dir?: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ dir }) => (dir === 'rtl' ? '' : 'flex-end !important')};
  text-align: ${({ dir }) => (dir === 'rtl' ? 'left !important' : 'right !important')};
`;

const FeeCapNote = styled.div<{ dir?: string }>`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  font-size: 12px !important;
  font-weight: 400;
  color: #8c8c8c;
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: ${({ dir }) => (dir === 'rtl' ? 'flex-start !important' : 'flex-end !important')};
  direction: ${({ dir }) => (dir === 'rtl' ? 'rtl' : 'ltr')};
  gap: 4px;
  white-space: nowrap;

  span {
    font-size: 12px;
    color: #8c8c8c;
  }

  .capAmount {
    display: inline-flex;
    align-items: center;

    svg .cls-1,
    svg path {
      fill: #8F8F8F;
    }
  }
`;

const FeeValue = styled.div<{ dir?: string }>`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #1a1a2e;
  text-align: ${({ dir }) => (dir === 'rtl' ? 'left !important' : 'right !important')};
  white-space: nowrap;

  @media (min-width: ${theme.breakpoints.sm}px) {
    font-size: 16px;
  }

  &.waived {
    color: #1a1a2e;
    font-weight: 700;
    direction: ${({ dir }) => (dir === 'rtl' ? 'rtl' : 'ltr')};
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  }
`;

const FeeValueInner = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  direction: ltr;
`;

const VatNote = styled.div<{ dir?: string }>`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  font-size: 14px;
  color: #8c8c8c;
  margin-top: 14px;
  margin-bottom: 6px;
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  direction: ${({ dir }) => dir ?? 'ltr'};

  @media (min-width: ${theme.breakpoints.sm}px) {
    margin-top: 16px;
    margin-bottom: 8px;
  }
`;

const FooterArea = styled.div`
  margin-top: 16px;

  @media (min-width: ${theme.breakpoints.sm}px) {
    margin-top: 24px;
    @media (max-width: ${theme.breakpoints.sm}px) {
        background: #FFF;
        box-shadow: 0 -2px 8px -2px var(--Shadow-boxShadow, rgba(0, 89, 150, 0.03)), 0 -8px 8px -2px var(--Shadow-boxShadow, rgba(0, 89, 150, 0.03));   
      }
  }
`;

const RetryBtn = styled(AntdButton)`
  padding: 0;
  font-size: 12px;
  color: ${colors.primaryColor};
`;

const AgreeButton = styled(AntdButton)`
  height: 52px !important;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;

  @media (min-width: ${theme.breakpoints.sm}px) {
    height: 48px!important;
    font-size: 15px;    
  }
`;

export default FeesAndChargesDrawer;
